/**
 * Klucze pamięci podręcznej react-query wypełniane przy renderowaniu po stronie serwera.
 *
 * Dlaczego osobne klucze, a nie te używane przez widoki:
 *
 * Widoki budują klucz z wartości, które na serwerze nie istnieją. W `useArtworksList`
 * jest to na przykład `[QueryKey.AuctionsList, offset, submittedFilters,
 * favoritesReadyState, initialPageNumber]`, gdzie `favoritesReadyState` startuje jako
 * `false` i przechodzi w `true` dopiero w `useEffect` — a efekty przy renderowaniu
 * serwerowym się nie wykonują. Odtwarzanie tych wartości po stronie serwera byłoby
 * kruche: każda zmiana w widoku po cichu rozjeżdżałaby klucz i SSR przestawałby działać
 * bez żadnego sygnału.
 *
 * Dlatego serwer zapisuje dane pod własnym, stabilnym kluczem, a widok czyta je
 * wyłącznie jako treść pierwszego renderowania. Cała logika kliencka — doładowywanie
 * kolejnych stron, filtry, polubienia — działa dalej na swoich kluczach, nietknięta.
 */
export const ssrQueryKeySuffix: string = 'ssr';

export function ssrQueryKey(key: string): [string, string] {
  return [key, ssrQueryKeySuffix];
}

/** Nazwa zmiennej globalnej, przez którą stan react-query trafia z serwera do przeglądarki. */
export const ssrStateWindowKey: string = '__SSR_QUERY_STATE__';

/**
 * Nazwa zmiennej globalnej ze stanem routingu wyliczonym po stronie serwera.
 *
 * Potrzebna, bo przy stronach o ładnych adresach (/dziela/nazwa-dziela) aplikacja
 * ustala identyfikator encji w `useEffect` w AppWrapper, przez wywołanie API.
 * Serwer robi to samo synchronicznie i przekazuje wynik, żeby pierwsze renderowanie
 * po obu stronach było identyczne — bez tego React zgłosiłby rozjazd.
 */
export const ssrRoutingStateWindowKey: string = '__SSR_ROUTING_STATE__';

/**
 * Wycinek stanu routingu przesyłany do przeglądarki.
 *
 * Świadomie NIE zawiera tablicy tras ani obiektu bieżącej trasy: pierwsza jest
 * stałą wkompilowaną w bundel, a druga wynika ze ścieżki adresu. Przesyłanie ich
 * powiększało odpowiedź o ~9 kB na każdej stronie szczegółów — przy kilku tysiącach
 * podstron to czysty narzut. Przekazujemy tylko to, czego przeglądarka nie odtworzy
 * sama: parametry wyciągnięte z adresu rzeczywistego oraz dopasowany wpis z bazy adresów.
 */
export interface SsrRoutingSnapshot {
  elementParamsFromRouting: Record<string, string>;
  currentWebsiteUrl: unknown;
}
